import React, { useState, useRef, useEffect } from 'react';
import { Mail, ChevronDown, Loader2, ArrowLeft, User, Lock, Eye, EyeOff, AtSign, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '@services/authService';
import { useAuthStore } from '@store/authStore';

type Step = 'email' | 'password' | 'otp' | 'name' | 'credentials' | '2fa' | 'set-password';

interface EmailLoginFormProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

const EmailLoginForm: React.FC<EmailLoginFormProps> = ({ isOpen, onOpenChange }) => {
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [fullName, setFullName] = useState('');
    const [username, setUsername] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isNewUser, setIsNewUser] = useState(false);
    const [isGoogleUser, setIsGoogleUser] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
    const login = useAuthStore(state => state.login);
    const isSubmittingRef = useRef(false);
    const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const usernameCheckTimeout = useRef<NodeJS.Timeout | null>(null);

    // Countdown timer for resend
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // Check username availability with debounce
    useEffect(() => {
        if (usernameCheckTimeout.current) {
            clearTimeout(usernameCheckTimeout.current);
        }

        if (username.length >= 3) {
            setUsernameStatus('checking');
            usernameCheckTimeout.current = setTimeout(async () => {
                try {
                    const response = await authService.checkUsername(username);
                    setUsernameStatus(response.available ? 'available' : 'taken');
                } catch {
                    setUsernameStatus('idle');
                }
            }, 500);
        } else {
            setUsernameStatus('idle');
        }

        return () => {
            if (usernameCheckTimeout.current) {
                clearTimeout(usernameCheckTimeout.current);
            }
        };
    }, [username]);

    const handleCheckEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmittingRef.current || isLoading) return;

        if (!email) {
            toast.error('Please enter your email address');
            return;
        }

        try {
            isSubmittingRef.current = true;
            setIsLoading(true);

            const response = await authService.checkEmail(email);

            if (response.exists) {
                setIsNewUser(false);
                setIsGoogleUser(response.is_google_user);

                if (!response.has_password) {
                    // User exists but has no password - send OTP to set password
                    await sendOTP();
                    setStep('otp');
                    toast.success('We\'ll send you a code to set your password');
                    return;
                }

                setStep('password');
            } else {
                // New user - send OTP for email verification
                setIsNewUser(true);
                await sendOTP();
                setStep('otp');
            }
        } catch (error: any) {
            console.error('Check email error:', error);
            toast.error(error?.response?.data?.error || 'Failed to check email');
        } finally {
            setIsLoading(false);
            isSubmittingRef.current = false;
        }
    };

    const sendOTP = async () => {
        try {
            const response = await authService.sendEmailOTP(email, 'verify');
            if (response.success) {
                setCountdown(60);
                toast.success('Verification code sent to your email');
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.error || 'Failed to send verification code');
            throw error;
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmittingRef.current || isLoading) return;

        if (!password) {
            toast.error('Please enter your password');
            return;
        }

        try {
            isSubmittingRef.current = true;
            setIsLoading(true);

            const response = await authService.emailLogin(email, password);

            if (response.requires_2fa) {
                setStep('2fa');
                setCountdown(60);
                toast.success('2FA code sent to your email');
            } else if (response.success && response.user && response.token) {
                login(response.user, response.token);
                toast.success('Login successful!');
            }
        } catch (error: any) {
            console.error('Login error:', error);
            toast.error(error?.response?.data?.error || 'Login failed');
        } finally {
            setIsLoading(false);
            isSubmittingRef.current = false;
        }
    };

    const handleOTPChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);

        if (value && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOTPPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newOtp = [...otp];
        for (let i = 0; i < pastedData.length; i++) {
            newOtp[i] = pastedData[i];
        }
        setOtp(newOtp);
        if (pastedData.length === 6) {
            otpInputRefs.current[5]?.focus();
        }
    };

    const handleVerifyOTP = async () => {
        if (isSubmittingRef.current || isLoading) return;

        const otpCode = otp.join('');
        if (otpCode.length !== 6) {
            toast.error('Please enter the 6-digit verification code');
            return;
        }

        try {
            isSubmittingRef.current = true;
            setIsLoading(true);

            if (step === '2fa') {
                // 2FA verification for login
                const response = await authService.verify2FA(email, otpCode);
                if (response.success) {
                    login(response.user, response.token);
                    toast.success('Login successful!');
                }
            } else {
                // Email verification for signup or password reset
                const response = await authService.verifyEmailOTP(email, otpCode);
                if (response.success) {
                    toast.success('Email verified!');
                    if (isNewUser) {
                        setStep('name');
                    } else {
                        // Existing user without password - let them set one
                        setStep('set-password');
                    }
                }
            }
        } catch (error: any) {
            console.error('Verify OTP error:', error);
            toast.error(error?.response?.data?.error || 'Verification failed');
        } finally {
            setIsLoading(false);
            isSubmittingRef.current = false;
        }
    };

    const handleNameSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!fullName.trim()) {
            toast.error('Please enter your name');
            return;
        }
        setUsername(email.split('@')[0]);
        setStep('credentials');
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmittingRef.current || isLoading) return;

        if (!username || username.length < 3) {
            toast.error('Username must be at least 3 characters');
            return;
        }

        if (usernameStatus === 'taken') {
            toast.error('This username is already taken');
            return;
        }

        if (!password || password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        try {
            isSubmittingRef.current = true;
            setIsLoading(true);

            const referralCode = sessionStorage.getItem('referral_code') || undefined;
            const response = await authService.register({
                email,
                username,
                password,
                full_name: fullName,
                referral_code: referralCode
            });

            if (response.success) {
                sessionStorage.removeItem('referral_code');
                login(response.user, response.token);
                toast.success('Account created successfully!');
            }
        } catch (error: any) {
            console.error('Register error:', error);
            toast.error(error?.response?.data?.error || 'Registration failed');
        } finally {
            setIsLoading(false);
            isSubmittingRef.current = false;
        }
    };

    const handleSetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmittingRef.current || isLoading) return;

        if (!password || password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        try {
            isSubmittingRef.current = true;
            setIsLoading(true);

            const response = await authService.setPassword(email, password);

            if (response.success && response.user && response.token) {
                login(response.user, response.token);
                toast.success('Password set successfully!');
            }
        } catch (error: any) {
            console.error('Set password error:', error);
            toast.error(error?.response?.data?.error || 'Failed to set password');
        } finally {
            setIsLoading(false);
            isSubmittingRef.current = false;
        }
    };

    const handleBack = () => {
        if (step === 'password' || step === 'otp') {
            setStep('email');
            setOtp(['', '', '', '', '', '']);
            setPassword('');
        } else if (step === '2fa') {
            setStep('password');
            setOtp(['', '', '', '', '', '']);
        } else if (step === 'name') {
            setStep('otp');
        } else if (step === 'credentials') {
            setStep('name');
        } else if (step === 'set-password') {
            setStep('email');
            setPassword('');
            setConfirmPassword('');
        }
    };

    const resetForm = () => {
        setStep('email');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setOtp(['', '', '', '', '', '']);
        setFullName('');
        setUsername('');
        setIsNewUser(false);
        setIsGoogleUser(false);
        setCountdown(0);
        setUsernameStatus('idle');
    };

    const handleToggle = () => {
        const newState = !isOpen;
        onOpenChange(newState);
        if (!newState) resetForm();
    };

    return (
        <div className="w-full relative flex flex-col items-center">
            <button
                onClick={handleToggle}
                type="button"
                className="w-full h-12 mb-3 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-all font-sans font-semibold text-sm flex items-center justify-center gap-2.5 active:scale-[0.98]"
            >
                <Mail size={18} className="text-primary-400/70" />
                Continue with Email
                <ChevronDown size={16} className={`transition-transform duration-300 text-white/60 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="w-full overflow-hidden">
                    {/* Email Step */}
                    {step === 'email' && (
                        <form onSubmit={handleCheckEmail} className="space-y-4 pb-4 px-1">
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-11 pr-4 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Enter your email..."
                                        required
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Checking...
                                    </>
                                ) : 'Continue'}
                            </button>
                        </form>
                    )}

                    {/* Password Step (for existing users) */}
                    {step === 'password' && (
                        <form onSubmit={handleLogin} className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                type="button"
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">Welcome back!</p>
                                <p className="text-primary-300 font-medium text-sm">{email}</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Password
                                </label>
                                <div className="relative">
                                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-11 pr-11 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Enter your password..."
                                        required
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Signing In...
                                    </>
                                ) : 'Sign In'}
                            </button>
                        </form>
                    )}

                    {/* OTP Step (for signup email verification or 2FA) */}
                    {(step === 'otp' || step === '2fa') && (
                        <div className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">
                                    {step === '2fa' ? 'Enter 2FA code sent to' : 'Enter the code sent to'}
                                </p>
                                <p className="text-primary-300 font-medium text-sm">{email}</p>
                            </div>

                            <div className="flex justify-center gap-2">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => { otpInputRefs.current[index] = el; }}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOTPChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOTPKeyDown(index, e)}
                                        onPaste={index === 0 ? handleOTPPaste : undefined}
                                        className="w-11 h-12 text-center bg-cosmic-950/50 border border-white/20 rounded-lg text-white text-lg font-mono focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors"
                                        disabled={isLoading}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={handleVerifyOTP}
                                disabled={isLoading || otp.join('').length !== 6}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Verifying...
                                    </>
                                ) : 'Verify Code'}
                            </button>

                            <div className="text-center">
                                {countdown > 0 ? (
                                    <p className="text-xs text-white/40">
                                        Resend code in {countdown}s
                                    </p>
                                ) : (
                                    <button
                                        onClick={sendOTP}
                                        disabled={isLoading}
                                        className="text-xs text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-50"
                                    >
                                        Resend code
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Name Step (for signup) */}
                    {step === 'name' && (
                        <form onSubmit={handleNameSubmit} className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                type="button"
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">
                                    Welcome! What should we call you?
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Full Name
                                </label>
                                <div className="relative">
                                    <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type="text"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        className="w-full pl-11 pr-4 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Enter your name..."
                                        required
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || !fullName.trim()}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                Continue
                            </button>
                        </form>
                    )}

                    {/* Credentials Step (username & password for signup) */}
                    {step === 'credentials' && (
                        <form onSubmit={handleRegister} className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                type="button"
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">
                                    Choose your username and password
                                </p>
                            </div>

                            {/* Username */}
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Username
                                </label>
                                <div className="relative">
                                    <AtSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                        className="w-full pl-11 pr-11 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Choose a username..."
                                        required
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                        {usernameStatus === 'checking' && (
                                            <Loader2 size={16} className="text-white/60 animate-spin" />
                                        )}
                                        {usernameStatus === 'available' && (
                                            <Check size={16} className="text-green-400" />
                                        )}
                                        {usernameStatus === 'taken' && (
                                            <X size={16} className="text-red-400" />
                                        )}
                                    </div>
                                </div>
                                {usernameStatus === 'taken' && (
                                    <p className="text-xs text-red-400 ml-1">This username is already taken</p>
                                )}
                            </div>

                            {/* Password */}
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Password
                                </label>
                                <div className="relative">
                                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-11 pr-11 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Create a password..."
                                        required
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-11 pr-4 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Confirm your password..."
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                                {confirmPassword && password !== confirmPassword && (
                                    <p className="text-xs text-red-400 ml-1">Passwords do not match</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || usernameStatus === 'taken' || password !== confirmPassword || password.length < 6}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Creating Account...
                                    </>
                                ) : 'Create Account'}
                            </button>
                        </form>
                    )}

                    {/* Set Password Step (for existing users without password) */}
                    {step === 'set-password' && (
                        <form onSubmit={handleSetPassword} className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                type="button"
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">
                                    Set a password for your account
                                </p>
                                <p className="text-primary-300 font-medium text-sm">{email}</p>
                            </div>

                            {/* Password */}
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    New Password
                                </label>
                                <div className="relative">
                                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-11 pr-11 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Create a password..."
                                        required
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-11 pr-4 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Confirm your password..."
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                                {confirmPassword && password !== confirmPassword && (
                                    <p className="text-xs text-red-400 ml-1">Passwords do not match</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || password !== confirmPassword || password.length < 6}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Setting Password...
                                    </>
                                ) : 'Set Password & Sign In'}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmailLoginForm;
